/**
 * Fullscreen viewer for Mermaid diagrams in the article body.
 *
 * After mermaid finishes drawing, every `.mermaid` block is wrapped in a
 * `<figure class="md-diagram">` carrying a hover-reveal expand button.
 * Clicking it clones the rendered SVG into the shared `#mermaidFullscreenModal`
 * and opens it through the standard modal stack, so Esc / focus trap / inert
 * background isolation / scroll lock / iOS swipe-back all come from
 * modalAccessibility + navigationController for free.
 *
 * Interaction model (generic across platforms, no per-device branches):
 *  - one pointer drag (mouse / pen / single touch finger) pans the diagram
 *  - two pointers pinch zoom around the fingers' midpoint
 *  - plain wheel (mouse wheel / trackpad two-finger scroll) pans natively
 *  - ctrl+wheel (trackpad pinch, ctrl+scroll) zooms toward the cursor
 *  - toolbar + / − / fit buttons zoom around the viewport center
 *
 * The diagram wrapper is JS-sized to `natural × fitScale × zoom` with explicit
 * margins (centered when it fits, edge-to-edge when it overflows), inside the
 * scrollable modal body, so panning = scrolling the body.
 *
 * Self-contained: call `wireMermaidFullscreenBlocks(container)` after mermaid
 * renders and this module handles everything else for that subtree.
 */
import { byId } from './domUtils';
import { openModal } from './navigationController';
import { ensureBusinessOverlayMounted } from './businessOverlayLifecycle';
import type { Root } from './prototypeTypes';

const EXPAND_LABEL = 'View diagram fullscreen';
const ZOOM_STEP = 1.5;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 8;
/** Wheel → zoom sensitivity for trackpad pinch / ctrl+scroll (per deltaY px). */
const WHEEL_ZOOM_FACTOR = 0.004;

const ICONS = {
	expand:
		'<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
		'<polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>' +
		'<line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>',
	zoomIn:
		'<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
		'<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
	zoomOut:
		'<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
		'<line x1="5" y1="12" x2="19" y2="12"/></svg>',
};

interface ViewerState {
	diagram: HTMLElement;
	naturalW: number;
	naturalH: number;
	fitScale: number;
	zoom: number;
}

/** Body-local focal point, used to keep a content point fixed while zooming. */
interface FocalPoint {
	x: number;
	y: number;
}

let viewerState: ViewerState | null = null;

/** Live pointer session: single-pointer pan and/or two-pointer pinch. */
interface PanTrack {
	startX: number;
	startY: number;
	sl: number;
	st: number;
}
interface PinchTrack {
	startDist: number;
	startZoom: number;
	startMidX: number;
	startMidY: number;
}
interface PointerSession {
	pointers: Map<number, { x: number; y: number }>;
	pan: PanTrack | null;
	pinch: PinchTrack | null;
}
let session: PointerSession | null = null;

function ensureFullscreenControl(block: HTMLElement): void {
	const hasSvg = block.querySelector('svg') !== null;
	let figure = block.closest<HTMLElement>('.md-diagram');
	if (!figure) {
		const created = document.createElement('figure');
		created.className = 'md-diagram';
		block.parentNode?.insertBefore(created, block);
		created.appendChild(block);
		figure = created;
	}
	const figureEl = figure;
	const existing = figureEl.querySelector<HTMLButtonElement>('.md-diagram-expand');
	if (hasSvg && !existing) {
		const expand = document.createElement('button');
		expand.type = 'button';
		expand.className = 'md-diagram-expand';
		expand.setAttribute('aria-label', EXPAND_LABEL);
		expand.title = EXPAND_LABEL;
		expand.innerHTML = ICONS.expand;
		expand.addEventListener('click', () => openMermaidFullscreen(figureEl));
		figureEl.appendChild(expand);
	} else if (!hasSvg && existing) {
		existing.remove();
	}
}

/**
 * Attach the fullscreen control to every `.mermaid` block under `container`.
 * Idempotent — re-runs after a theme-driven mermaid re-render are no-ops.
 */
export function wireMermaidFullscreenBlocks(container: Root): void {
	container.querySelectorAll<HTMLElement>('.mermaid').forEach(ensureFullscreenControl);
}

/** Clone the diagram's rendered SVG into the fullscreen modal and open it. */
export function openMermaidFullscreen(figure: HTMLElement): void {
	const root = figure.ownerDocument;
	ensureBusinessOverlayMounted(root, 'mermaidFullscreenModal');
	const svg = figure.querySelector<SVGSVGElement>('.mermaid svg');
	const body = byId(root, 'mermaidFullscreenBody');
	if (!svg || !body) return;
	session = null;
	const viewBox = svg.viewBox?.baseVal;
	const naturalW = viewBox && viewBox.width > 0 ? viewBox.width : svg.clientWidth || 1;
	const naturalH = viewBox && viewBox.height > 0 ? viewBox.height : svg.clientHeight || 1;
	// Reuse the `.mermaid` class so the dark-theme sequence-diagram color
	// overrides (article-reading.css) also apply to the enlarged clone.
	const wrapper = document.createElement('div');
	wrapper.className = 'mermaid mermaid-fullscreen-diagram';
	wrapper.appendChild(svg.cloneNode(true));
	body.replaceChildren(wrapper);
	body.scrollLeft = 0;
	body.scrollTop = 0;
	// A zero fitScale marks the first layout pass. It is important that the
	// initial `applyZoom(…, 1)` does real work: at 100% the viewer must still
	// size the wrapper to the available viewport before any toolbar gesture.
	viewerState = { diagram: wrapper, naturalW, naturalH, fitScale: 0, zoom: 1 };
	installViewerControls(root);
	syncZoomLabel();
	openModal(root, 'mermaidFullscreenModal', figure.querySelector<HTMLElement>('.md-diagram-expand'));
	requestAnimationFrame(() => {
		if (viewerState) applyZoom(viewerState, 1);
	});
}

/**
 * Resize the wrapper to `natural × fitScale × zoom` and, when a focal point is
 * given, keep the content point currently under that focal fixed (so zoom is
 * anchored at the cursor / pinch midpoint / viewport center instead of the
 * wrapper corner). Panning is the modal body's own scrolling.
 */
function applyZoom(state: ViewerState, zoom: number, focal?: FocalPoint): void {
	const body = byId(document, 'mermaidFullscreenBody');
	if (!body || state.diagram !== body.firstElementChild) return;
	const prev = state.zoom;
	const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));
	if (next === prev && state.fitScale > 0) return;
	const cw = body.clientWidth;
	const ch = body.clientHeight;
	state.fitScale = Math.min(cw / state.naturalW, ch / state.naturalH);
	const k = next / prev;
	const oldScale = state.fitScale * prev;
	const newScale = state.fitScale * next;
	const oldMl = Math.max(0, (cw - state.naturalW * oldScale) / 2);
	const oldMt = Math.max(0, (ch - state.naturalH * oldScale) / 2);
	const newMl = Math.max(0, (cw - state.naturalW * newScale) / 2);
	const newMt = Math.max(0, (ch - state.naturalH * newScale) / 2);
	const sl = body.scrollLeft;
	const st = body.scrollTop;
	state.zoom = next;
	state.diagram.style.width = `${Math.round(state.naturalW * newScale)}px`;
	state.diagram.style.height = `${Math.round(state.naturalH * newScale)}px`;
	state.diagram.style.marginLeft = `${Math.round(newMl)}px`;
	state.diagram.style.marginTop = `${Math.round(newMt)}px`;
	if (focal) {
		body.scrollLeft = newMl + (focal.x + sl - oldMl) * k - focal.x;
		body.scrollTop = newMt + (focal.y + st - oldMt) * k - focal.y;
	}
	syncZoomLabel();
}

function syncZoomLabel(): void {
	const label = byId(document, 'mfZoomLevel');
	if (label && viewerState) label.textContent = `${Math.round(viewerState.zoom * 100)}%`;
}

function isActive(): boolean {
	const modal = byId(document, 'mermaidFullscreenModal');
	return Boolean(viewerState && modal?.classList.contains('active'));
}

function bodyCenterFocal(): FocalPoint {
	const body = byId(document, 'mermaidFullscreenBody');
	return { x: (body?.clientWidth ?? 0) / 2, y: (body?.clientHeight ?? 0) / 2 };
}

function onPointerDown(e: PointerEvent): void {
	if (!isActive()) {
		session = null;
		return;
	}
	const body = byId(document, 'mermaidFullscreenBody');
	if (!body) return;
	if (!session) session = { pointers: new Map(), pan: null, pinch: null };
	session.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
	if (session.pointers.size === 1) {
		session.pan = { startX: e.clientX, startY: e.clientY, sl: body.scrollLeft, st: body.scrollTop };
		session.pinch = null;
		body.classList.add('mf-dragging');
	} else if (session.pointers.size === 2 && viewerState) {
		const [a, b] = [...session.pointers.values()];
		session.pinch = {
			startDist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
			startZoom: viewerState.zoom,
			startMidX: (a.x + b.x) / 2,
			startMidY: (a.y + b.y) / 2,
		};
		session.pan = null;
	}
	body.setPointerCapture?.(e.pointerId);
	e.preventDefault();
}

function onPointerMove(e: PointerEvent): void {
	if (!isActive() || !session) return;
	const body = byId(document, 'mermaidFullscreenBody');
	const state = viewerState;
	if (!body || !state) return;
	const prev = session.pointers.get(e.pointerId);
	if (!prev) return;
	const cur = { x: e.clientX, y: e.clientY };
	session.pointers.set(e.pointerId, cur);
	if (session.pinch && session.pointers.size >= 2) {
		const [a, b] = [...session.pointers.values()];
		const dist = Math.hypot(a.x - b.x, a.y - b.y);
		const rect = body.getBoundingClientRect();
		// Zoom around the fingers' start midpoint, then follow the midpoint's
		// own travel so the diagram tracks the hands.
		const midX = session.pinch.startMidX - rect.left;
		const midY = session.pinch.startMidY - rect.top;
		applyZoom(state, (session.pinch.startZoom * dist) / session.pinch.startDist, { x: midX, y: midY });
		const curMidX = (a.x + b.x) / 2;
		const curMidY = (a.y + b.y) / 2;
		body.scrollLeft -= curMidX - session.pinch.startMidX;
		body.scrollTop -= curMidY - session.pinch.startMidY;
	} else if (session.pan) {
		body.scrollLeft = session.pan.sl - (cur.x - session.pan.startX);
		body.scrollTop = session.pan.st - (cur.y - session.pan.startY);
	}
	e.preventDefault();
}

function onPointerUp(e: PointerEvent): void {
	if (!session) return;
	session.pointers.delete(e.pointerId);
	const body = byId(document, 'mermaidFullscreenBody');
	if (session.pointers.size === 0) {
		session = null;
		body?.classList.remove('mf-dragging');
	} else if (session.pointers.size === 1 && body) {
		const [only] = [...session.pointers.values()];
		session.pinch = null;
		session.pan = { startX: only.x, startY: only.y, sl: body.scrollLeft, st: body.scrollTop };
	}
}

function onWheel(e: WheelEvent): void {
	if (!isActive()) return;
	if (!e.ctrlKey) return; // plain wheel / trackpad two-finger scroll pans via native scroll
	// Trackpad pinch and ctrl+scroll both arrive as ctrlKey wheel events → zoom
	// toward the cursor. Without preventDefault the page would zoom instead.
	e.preventDefault();
	const body = byId(document, 'mermaidFullscreenBody');
	if (!body || !viewerState) return;
	const rect = body.getBoundingClientRect();
	const factor = Math.exp(-e.deltaY * WHEEL_ZOOM_FACTOR);
	applyZoom(viewerState, viewerState.zoom * factor, { x: e.clientX - rect.left, y: e.clientY - rect.top });
}

// Controls are mounted with the modal body. Tracking the body (rather than
// only the document) also stays correct if the SPA root is remounted.
const installedControlBodies = new WeakSet<HTMLElement>();

function installViewerControls(root: Document): void {
	const body = byId(root, 'mermaidFullscreenBody');
	// markdownRenderer may be imported before the static modal markup is
	// mounted. Do not mark the document installed until the actual controls
	// exist; openMermaidFullscreen retries after the modal is present.
	if (!body || installedControlBodies.has(body)) return;
	const wire = (id: string, fn: () => void) => {
		byId(root, id)?.addEventListener('click', fn);
	};
	wire('mfZoomIn', () => {
		if (viewerState) applyZoom(viewerState, viewerState.zoom * ZOOM_STEP, bodyCenterFocal());
	});
	wire('mfZoomOut', () => {
		if (viewerState) applyZoom(viewerState, viewerState.zoom / ZOOM_STEP, bodyCenterFocal());
	});
	wire('mfZoomFit', () => {
		if (viewerState) applyZoom(viewerState, 1, bodyCenterFocal());
	});
	body?.addEventListener('pointerdown', onPointerDown);
	body?.addEventListener('pointermove', onPointerMove);
	body?.addEventListener('pointerup', onPointerUp);
	body?.addEventListener('pointercancel', onPointerUp);
	body?.addEventListener('lostpointercapture', onPointerUp);
	body?.addEventListener('wheel', onWheel, { passive: false });
	installedControlBodies.add(body);
}

if (typeof document !== 'undefined') installViewerControls(document);
